import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { POLICIES, defaultPolicy, shopFacts } from "@/lib/policies";
import { useTenant } from "./tenantContext";
import ShopFooter, { useShopBrand } from "./ShopFooter";

/** /shop/policy/:kind and /shop/:slug/policy/:kind */
export default function ShopPolicy() {
  const { kind } = useParams();
  const { slug } = useTenant();
  const brand = useShopBrand();

  const policy = POLICIES.find((p) => p.key === kind) || POLICIES[0];
  const f = shopFacts(brand);
  const custom = (brand?.[`policy_${policy.key}`] || "").trim();
  const home = slug ? `/shop/${slug}` : "/shop";

  useEffect(() => {
    document.title = `${policy.label} · ${f.shop}`;
  }, [policy.label, f.shop]);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid={`shop-policy-${policy.key}`}>
      <div className="max-w-3xl w-full mx-auto px-4 py-6 flex-1">
        <Link to={home} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4" /> Back to {f.shop}
        </Link>
        <h1 className="font-heading text-3xl font-bold mt-4">{policy.label}</h1>
        <div className="text-xs text-slate-500 mt-1">{f.legal}</div>

        {policy.key === "contact" ? (
          <div className="mt-6 space-y-4 text-slate-700">
            <p>We&apos;re happy to help with orders, returns, payments or product questions.</p>
            <div className="rounded-xl border border-slate-200 divide-y">
              <div className="p-4">
                <div className="text-xs uppercase tracking-wider text-slate-500">Business</div>
                <div className="font-medium mt-0.5">{f.legal}</div>
                {f.gstin && <div className="text-sm text-slate-500 mt-0.5">GSTIN: {f.gstin}</div>}
              </div>
              {f.address && (
                <div className="p-4 flex gap-3">
                  <MapPin className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                  <div>{f.address}</div>
                </div>
              )}
              {f.phone && (
                <a href={`tel:${f.phone.replace(/\s/g, "")}`} className="p-4 flex gap-3 hover:bg-slate-50">
                  <Phone className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                  <div>{f.phone}</div>
                </a>
              )}
              {brand?.whatsapp_number && (
                <a
                  href={`https://wa.me/${brand.whatsapp_number.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="p-4 flex gap-3 hover:bg-slate-50"
                >
                  <MessageCircle className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                  <div>WhatsApp {brand.whatsapp_number}</div>
                </a>
              )}
              {f.email && (
                <a href={`mailto:${f.email}`} className="p-4 flex gap-3 hover:bg-slate-50">
                  <Mail className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                  <div>{f.email}</div>
                </a>
              )}
            </div>
            <p className="text-sm text-slate-500">We usually reply within one working day.</p>
          </div>
        ) : custom ? (
          <div className="mt-6 whitespace-pre-line text-slate-700 leading-relaxed">{custom}</div>
        ) : (
          <div className="mt-6 space-y-5 text-slate-700 leading-relaxed">
            {defaultPolicy(policy.key, brand).map(([heading, text]) => (
              <section key={heading}>
                <h2 className="font-semibold text-slate-900">{heading}</h2>
                <p className="mt-1">{text}</p>
              </section>
            ))}
          </div>
        )}
      </div>
      <ShopFooter />
    </div>
  );
}
